import { AlertTriangle, Loader2, SpellCheck } from "lucide-react";

export function buildSpellingFixNote(issues) {
  const parts = (issues || [])
    .filter((i) => i?.found)
    .map((i) =>
      i.should_be
        ? `change "${i.found}" to "${i.should_be}"`
        : `fix the misspelled/garbled text "${i.found}"`,
    );
  return (
    `Fix the spelling of the on-image text: ${parts.join("; ")}. ` +
    "Spell every word PERFECTLY. Keep the composition, characters, " +
    "colors and everything else exactly the same."
  );
}

/**
 * SpellcheckBadge — renders the persisted vision spell-check result
 * for a scene image or thumbnail. Amber warning + one-click
 * "Fix & re-render" when issues exist; subtle green tick when the
 * image has text and it's all spelled correctly; nothing otherwise.
 */
export default function SpellcheckBadge({ spellcheck, onFix, busy, testidPrefix = "spellcheck" }) {
  if (!spellcheck || spellcheck.ok === false) return null;
  const issues = spellcheck.issues || [];
  if (spellcheck.clean || issues.length === 0) {
    if (!spellcheck.has_text) return null;
    return (
      <div
        data-testid={`${testidPrefix}-clean`}
        className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-emerald-400"
      >
        <SpellCheck className="w-3 h-3" /> on-image text spelled correctly
      </div>
    );
  }
  return (
    <div
      data-testid={`${testidPrefix}-issues`}
      className="border border-amber-400/50 bg-amber-400/10 rounded-sm p-3 space-y-2"
    >
      <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-amber-300 font-bold">
        <AlertTriangle className="w-3.5 h-3.5" /> Spelling issues detected
      </div>
      <ul className="space-y-0.5">
        {issues.map((i, idx) => (
          <li key={idx} className="text-xs text-amber-100 font-mono">
            "{i.found}"
            {i.should_be ? (
              <>
                {" "}→ <span className="text-emerald-300">"{i.should_be}"</span>
              </>
            ) : (
              " (garbled text)"
            )}
          </li>
        ))}
      </ul>
      {onFix && (
        <button
          type="button"
          data-testid={`${testidPrefix}-fix-btn`}
          onClick={() => onFix(buildSpellingFixNote(issues))}
          disabled={busy}
          className="inline-flex items-center gap-2 font-mono text-[11px] font-bold uppercase tracking-[0.2em] border-2 border-amber-400 hover:border-amber-300 text-black bg-amber-400 hover:bg-amber-300 px-3 py-1.5 rounded-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <SpellCheck className="w-3.5 h-3.5" />}
          Fix spelling & re-render
        </button>
      )}
    </div>
  );
}
